// components/auth/RouteGuard.tsx

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from './authService';

interface RouteGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

const RouteGuard = ({ children, requireAuth = true }: RouteGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<number>(0);
  const currentLocationRef = useRef<string>('');

  useEffect(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Check if location actually changed
    const currentPath = location.pathname + location.search;
    if (currentLocationRef.current === currentPath) {
      return;
    }
    currentLocationRef.current = currentPath;

    // Debounce auth checks to prevent rapid successive calls
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckRef.current;
    const MIN_CHECK_INTERVAL = 1000; // 1 second minimum between checks

    const checkAuth = async () => {
      try {
        lastCheckRef.current = Date.now();
        console.log('RouteGuard: Checking authentication for path:', currentPath);

        // First check if we have basic auth data locally
        const hasLocalAuth = authService.isAuthenticated();

        if (requireAuth) {
          if (!hasLocalAuth) {
            // No local auth, redirect to login immediately
            const currentPath = location.pathname || '/';
            if (!currentPath.includes('/login')) {
              console.log('RouteGuard: No local auth, redirecting to login');
              navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
              return;
            }
          } else {
            // Has local auth, validate with backend (uses cache if available)
            console.log('RouteGuard: Validating token with backend');
            const isValidToken = await authService.validateToken();
            if (!isValidToken) {
              // Token is invalid, clear auth and redirect to login
              console.log('RouteGuard: Token invalid, clearing auth and redirecting');
              authService.logout();
              const currentPath = location.pathname || '/';
              navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
              return;
            }
            console.log('RouteGuard: Token valid, user authenticated');
            setIsAuthenticated(true);
          }
        } else {
          // This is a public route (like login page)
          if (hasLocalAuth) {
            // Validate token before redirecting (uses cache if available)
            console.log('RouteGuard: Public route, but user has local auth, validating');
            const isValidToken = await authService.validateToken();
            if (isValidToken) {
              // User is authenticated, redirect away from login
              console.log('RouteGuard: User authenticated on public route, redirecting to dashboard');
              const redirectPath = new URLSearchParams(location.search).get('redirect');
              if (redirectPath && !redirectPath.includes('/login')) {
                navigate(redirectPath);
              } else {
                navigate('/dashboard');
              }
              return;
            } else {
              // Invalid token, clear auth data
              console.log('RouteGuard: Invalid token on public route, clearing auth');
              authService.logout();
              setIsAuthenticated(false);
            }
          } else {
            console.log('RouteGuard: Public route, no local auth');
            setIsAuthenticated(false);
          }
        }

        setIsChecking(false);
      } catch (error) {
        console.error('RouteGuard: Auth check error:', error);
        setIsChecking(false);
        if (requireAuth) {
          authService.logout();
          navigate('/login');
        }
      }
    };

    // If enough time has passed since last check or this is first check, run immediately
    if (timeSinceLastCheck >= MIN_CHECK_INTERVAL || lastCheckRef.current === 0) {
      checkAuth();
    } else {
      // Otherwise, debounce the check
      const delay = MIN_CHECK_INTERVAL - timeSinceLastCheck;
      console.log(`RouteGuard: Debouncing auth check by ${delay}ms`);
      timeoutRef.current = setTimeout(checkAuth, delay);
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [requireAuth, navigate, location]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Redirecting to login...</span>
      </div>
    );
  }

  if (!requireAuth && isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Redirecting to dashboard...</span>
      </div>
    );
  }

  return <>{children}</>;
};

export default RouteGuard;